import { api } from "./api";

export enum NotificationType {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
  SUCCESS = "success",
  INVENTORY_REORDER = "inventory_reorder",
}

export enum NotificationCategory {
  INVENTORY = "inventory",
  SALES = "sales",
  PURCHASE = "purchase",
  MANUFACTURING = "manufacturing",
  FINANCE = "finance",
  SYSTEM = "system",
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  category: NotificationCategory;
  metadata?: Record<string, any> | null;
  userId: string;
  isRead: boolean;
  readAt?: string | null;
  // A page of this application to open
  actionUrl?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationStats {
  total: number;
  unread: number;
  // Arrived since midnight
  today: number;
  byType: Record<NotificationType, number>;
  byCategory: Record<NotificationCategory, number>;
  unreadByCategory: Record<NotificationCategory, number>;
}

export interface NotificationPage {
  data: Notification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateNotificationDto {
  title: string;
  message: string;
  type?: NotificationType;
  category?: NotificationCategory;
  metadata?: Record<string, any>;
  actionUrl?: string;
  expiresAt?: string;
}

export interface QueryNotificationsDto {
  type?: NotificationType;
  category?: NotificationCategory;
  isRead?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));

export class NotificationsService {
  static async getNotifications(params: QueryNotificationsDto = {}): Promise<NotificationPage> {
    const response = await api.get("/notifications", { params: clean(params) });
    return response.data;
  }

  static async getNotification(id: string): Promise<Notification> {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  }

  static async createNotification(data: CreateNotificationDto): Promise<Notification> {
    const response = await api.post("/notifications", data);
    return response.data;
  }

  static async markAsRead(id: string): Promise<Notification> {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  }

  static async markAsUnread(id: string): Promise<Notification> {
    const response = await api.patch(`/notifications/${id}/unread`);
    return response.data;
  }

  static async markAllAsRead(): Promise<{ affected: number }> {
    const response = await api.patch("/notifications/mark-all-read");
    return response.data;
  }

  // Marks several notifications read or unread, or deletes them
  static async bulk(ids: string[], action: "read" | "unread" | "delete"): Promise<{ affected: number }> {
    const response = await api.post("/notifications/bulk", { ids, action });
    return response.data;
  }

  static async deleteNotification(id: string): Promise<void> {
    await api.delete(`/notifications/${id}`);
  }

  // Deletes what was read, or everything
  static async deleteAllNotifications(scope: "read" | "all" = "read"): Promise<{ deleted: number }> {
    const response = await api.delete("/notifications", { params: { scope } });
    return response.data;
  }

  static async getStats(): Promise<NotificationStats> {
    const response = await api.get("/notifications/stats");
    return response.data;
  }

  // Checks the business data for alerts now instead of waiting for the next check
  static async scan(): Promise<{ raised: number; resolved: number }> {
    const response = await api.post("/notifications/scan");
    return response.data;
  }
}
